import { useEffect, useState } from 'react';
import api from '@/api/client';
import { useToast } from '@/components/ui/Toast';
import { useAppConfig } from '@/hooks/useAppConfig';
import { VenetianMask } from 'lucide-react';

/**
 * Sticky banner at the top of the app while a dev-tools impersonation is
 * active. Only ever renders on dev environments (gated by /api/config).
 *
 * Polls the backend every 30s so it picks up impersonation changes made
 * via another tab.
 */
export function ImpersonationBanner() {
  const config = useAppConfig();
  const { toast } = useToast();
  const [role, setRole] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!config?.devTools) return;
    let cancelled = false;
    async function check() {
      try {
        const r = await api.get('/dev-tools/impersonation');
        if (!cancelled) setRole(r.data.impersonating ? r.data.role : null);
      } catch {
        if (!cancelled) setRole(null);
      }
    }
    check();
    const t = setInterval(check, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [config?.devTools]);

  async function handleStop() {
    setBusy(true);
    try {
      await api.post('/dev-tools/stop-impersonating');
      setRole(null);
      toast('Impersonation stopped — back to your real role', 'success');
      // Reload so all the page's role-based UI re-evaluates
      setTimeout(() => window.location.reload(), 300);
    } catch (err: any) {
      toast(err?.response?.data?.error ?? 'Failed to stop impersonating', 'error');
    } finally {
      setBusy(false);
    }
  }

  if (!config?.devTools || !role) return null;

  return (
    <div style={banner}>
      <span style={{ fontSize: 13, color: '#fff', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <VenetianMask size={16} /> Viewing as <strong style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{role}</strong> — your real role is hidden for this session
      </span>
      <button onClick={handleStop} disabled={busy} style={stopBtn}>
        {busy ? 'Stopping...' : 'Stop impersonating'}
      </button>
    </div>
  );
}

const banner: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 1000,
  background: '#9333ea',
  padding: '8px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 16,
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  flexWrap: 'wrap',
};

const stopBtn: React.CSSProperties = {
  background: '#fff',
  color: '#9333ea',
  border: 'none',
  borderRadius: 6,
  padding: '4px 14px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
};
